/**
 * AddRemoveSymbolsDialog — Add or remove tracked FNO underlyings
 * Shows tracked symbols with Remove button.
 * To add a new symbol: search the Instruments table, pick one, click Add.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Chip, CircularProgress,
  IconButton, Divider, Alert, InputAdornment, Paper, List, ListItem,
  ListItemText, ListItemSecondaryAction
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';
import { optionsService } from '../../services/options.service';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Underlying {
  id: number;
  symbol: string;
  displayName?: string;
  exchange: string;
  isTracked: boolean;
  feedSource: string;
  lotSize: number;
}

interface InstrumentResult {
  symbol: string;
  exchange: string;
  name?: string;
  lotSize?: number;
  instrumentType?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const EXCHANGE_COLORS_LIGHT: Record<string, { bg: string; color: string }> = {
  NSE:   { bg: '#e8f5e9', color: '#1b5e20' },
  NFO:   { bg: '#e3f2fd', color: '#0d47a1' },
  BSE:   { bg: '#fff3e0', color: '#e65100' },
  MCX:   { bg: '#f3e5f5', color: '#4a148c' },
  DELTA: { bg: '#e0f7fa', color: '#006064' },
};
const EXCHANGE_COLORS_DARK: Record<string, { bg: string; color: string }> = {
  NSE:   { bg: 'rgba(45,212,170,0.15)', color: '#2DD4AA' },
  NFO:   { bg: 'rgba(96,165,250,0.15)', color: '#60A5FA' },
  BSE:   { bg: 'rgba(251,191,36,0.15)', color: '#FBBF24' },
  MCX:   { bg: 'rgba(167,139,250,0.15)', color: '#A78BFA' },
  DELTA: { bg: 'rgba(96,165,250,0.12)', color: '#60A5FA' },
};

const ManageSymbolsDialog: React.FC<Props> = ({ open, onClose, onChanged }) => {
  const { C, isDark } = useAppTheme();
  const exchStyle = (exchange: string) => {
    const map = isDark ? EXCHANGE_COLORS_DARK : EXCHANGE_COLORS_LIGHT;
    return map[exchange] || (isDark ? { bg: 'rgba(110,118,129,0.15)', color: '#8B949E' } : { bg: '#f5f5f5', color: '#333' });
  };

  const [tracked, setTracked] = useState<Underlying[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Instrument search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<InstrumentResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const loadTracked = useCallback(async () => {
    setLoading(true);
    try {
      const res = await optionsService.searchUnderlyings('');
      const all: Underlying[] = (res.data as any).data || [];
      setTracked(all.filter(u => u.isTracked));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { loadTracked(); setSearchQuery(''); setSearchResults([]); }
  }, [open, loadTracked]);

  // Debounce instrument search
  useEffect(() => {
    if (!open || !searchQuery.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await api.get('/instruments', { params: { search: searchQuery, pageSize: 20 } });
        const rData = (r as any).data;
        const items: any[] = rData?.data?.instruments || rData?.instruments || [];
        // Exclude individual option contracts (CE/PE) — we only want the underlying
        // For MCX futures (e.g. CRUDEOILMAY25), use the `name` field as the underlying symbol
        const EXCLUDED_TYPES = ['CE', 'PE', 'MF', 'MFO'];
        const seen = new Set<string>();
        const mapped: InstrumentResult[] = [];
        for (const inst of items) {
          const itype = (inst.instrumentType || '').toUpperCase();
          if (EXCLUDED_TYPES.includes(itype)) continue;
          // For FUT instruments the tradingsymbol includes expiry (CRUDEOILMAY25);
          // use the `name` field (CRUDEOIL) as the underlying symbol instead.
          const underlyingSymbol = itype === 'FUT' && inst.name
            ? inst.name.toUpperCase().trim()
            : inst.symbol;
          const key = `${underlyingSymbol}|${inst.exchange}`;
          if (seen.has(key)) continue; // deduplicate (multiple expiries for MCX FUT)
          seen.add(key);
          mapped.push({
            symbol: underlyingSymbol,
            exchange: inst.exchange || '',
            name: itype === 'FUT' ? `${underlyingSymbol} (${inst.exchange} commodity)` : inst.name || '',
            lotSize: inst.lotSize ? Number(inst.lotSize) : undefined,
            instrumentType: itype,
          });
        }
        setSearchResults(mapped);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, open]);

  const handleRemove = async (u: Underlying) => {
    setActionLoading(u.id);
    try {
      await optionsService.removeUnderlying(u.id);
      await loadTracked();
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddFromSearch = async (inst: InstrumentResult) => {
    const key = `${inst.symbol}-${inst.exchange}`;
    setActionLoading(key);
    setError(null);
    try {
      // Send symbol + exchange + lotSize; the backend auto-derives zerodhaExchange,
      // isIndex, and feedSource correctly for NSE stocks, MCX commodities, and Delta.
      await optionsService.addUnderlying({
        symbol: inst.symbol,
        displayName: inst.name || inst.symbol,
        exchange: inst.exchange,
        lotSize: inst.lotSize || 1,
      });
      setSearchQuery('');
      setSearchResults([]);
      await loadTracked();
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const alreadyTrackedSet = new Set(tracked.map(u => `${u.symbol}|${u.exchange}`));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add / Remove FNO Symbols</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* ── Tracked symbols ── */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
          TRACKED ({tracked.length})
        </Typography>
        <Box sx={{ mt: 0.5, mb: 2.5, maxHeight: 180, overflowY: 'auto' }}>
          {loading ? (
            <CircularProgress size={20} sx={{ display: 'block', my: 1 }} />
          ) : tracked.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>No tracked symbols. Add one below.</Typography>
          ) : tracked.map(u => {
            const style = exchStyle(u.exchange);
            return (
              <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{u.symbol}</Typography>
                  <Chip size="small" label={u.exchange}
                    sx={{ fontSize: '0.62rem', height: 18, bgcolor: style.bg, color: style.color, fontWeight: 600 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>lot={u.lotSize}</Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemove(u)}
                  disabled={actionLoading === u.id}
                  sx={{ color: C.bear }}
                >
                  {actionLoading === u.id ? <CircularProgress size={14} /> : <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* ── Add from Instruments ── */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
          ADD SYMBOL — search instruments
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Type symbol name (e.g. NIFTY, BANKNIFTY, CRUDEOIL)…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value.toUpperCase())}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment>,
            endAdornment: searchLoading ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment> : null,
          }}
          sx={{ mt: 0.75, mb: 1 }}
        />

        {searchResults.length > 0 && (
          <Paper variant="outlined" sx={{ maxHeight: 220, overflowY: 'auto', borderRadius: 1 }}>
            <List dense disablePadding>
              {searchResults.map((inst, i) => {
                const key = `${inst.symbol}-${inst.exchange}`;
                const alreadyTracked = alreadyTrackedSet.has(`${inst.symbol}|${inst.exchange}`);
                const style = exchStyle(inst.exchange);
                return (
                  <ListItem
                    key={key}
                    divider={i < searchResults.length - 1}
                    sx={{ py: 0.75 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{inst.symbol}</Typography>
                          <Chip size="small" label={inst.exchange}
                            sx={{ fontSize: '0.62rem', height: 18, bgcolor: style.bg, color: style.color, fontWeight: 600 }} />
                          {inst.lotSize && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>lot={inst.lotSize}</Typography>
                          )}
                        </Box>
                      }
                      secondary={inst.name || undefined}
                      secondaryTypographyProps={{ variant: 'caption', sx: { color: 'text.secondary' } }}
                    />
                    <ListItemSecondaryAction>
                      {alreadyTracked ? (
                        <Chip size="small" label="Tracked" sx={{ fontSize: '0.62rem', height: 18, color: C.bull, borderColor: C.bullBorder }} variant="outlined" />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={actionLoading === key ? <CircularProgress size={12} /> : <AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleAddFromSearch(inst)}
                          disabled={actionLoading === key}
                          sx={{ fontSize: '0.7rem', py: 0.25, color: C.bull, borderColor: C.bullBorder }}
                        >
                          Add
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}

        {searchQuery.length > 0 && searchResults.length === 0 && !searchLoading && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            No instruments found for "{searchQuery}". Try a shorter or different name.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageSymbolsDialog;
